import {StatementsEvaluator} from '../statements/statements-evaluator/statements-evaluator';
import {ObjectEvaluator} from '../value-evaluators/object-evaluator/object-evaluator';
import {ArrayEvaluator} from '../value-evaluators/array-evaluator/array-evaluator';
import {SimpleValueEvaluator} from '../value-evaluators/simple-value-evaluator/simple-value-evaluator';
import {IValueEvaluator} from '../value-evaluators/value-evaluator.interface/value-evaluator.interface';
import {IConfigurationEvaluationContext} from './configuration-evaluation-context.interface';
import {Check} from '@sparkware/app-utils';
import {IStatementEvaluator} from '../statements/statement-evaluator/statement-evaluator.interface';
import {StatementEvaluator} from '../statements/statement-evaluator/statement-evaluator';
import {ILogicalOperator} from '../statements/logical-operators/logical-operator.interface/logical-operator.interface';
import {ISegmentationRule} from '../statements/segmentation-rule/segmentation-rule.interface';
import {OrLogicalOperator} from '../statements/logical-operators/or-logical-operator/or-logical-operator';
import {AndLogicalOperator} from '../statements/logical-operators/and-logical-operator/and-logical-operator';
import {SegmentationRule} from '../statements/segmentation-rule/segmentation-rule';
import {IComparisonOperator} from '../statements/comparison-operators/comparison-operator.interface/comparison-operator.interface';
import {NotEqualOperator} from '../statements/comparison-operators/not-equal-operator/not-equal-operator';
import {EqualOperator} from '../statements/comparison-operators/equal-operator/equal-operator';
import {PropertyLevelContext} from '../property-level-context/property-level-context';
import {PropertyEvaluator} from '../property-evaluator/property-evaluator';
import {IPropertyEvaluator} from '../property-evaluator/property-evaluator.interface';
import {GamesIdPropertyEvaluator} from '../special-properties-evaluators/games-id-property-evaluator/games-id-property-evaluator';
import {IsFalsyOperator} from "../statements/comparison-operators/is-falsy-operator/is-falsy-operator";
import {IConfigurationEvaluationDataProvider} from './configuration-evaluation-data-provider';
import {GreaterThanOperator} from "../statements/comparison-operators/greater-than-operator/greater-than-operator";
import {LessThanOperator} from "../statements/comparison-operators/less-than-operator/less-than-operator";
import {AnyOfOperator} from "../statements/comparison-operators/any-of-operator/any-of-operator";
import {isInsideAutomation} from '../../../../../utils/automation-helpers';


export function updateAssetPath(assetPath: string, configurationPath: string): string {
    return assetPath.replace(/{assetsPath}/gi, configurationPath + '/assets');
}

export class ConfigurationEvaluationContext implements IConfigurationEvaluationContext {
  constructor(private dataProvider: IConfigurationEvaluationDataProvider) {

  }



  getSegmentValue(name: string): any {
    return this.dataProvider.getSegmentValue(name);
  }

  parsePropertyValue(value: any): IValueEvaluator {
    if(Check.isObject(value)) {
      if(this._hasStatementsOrDefaultValue(value)) {
        return new StatementsEvaluator(this._createStatementsEvaluators(value.statements), value.defaultValue);
      } else {
        return new ObjectEvaluator(value);
      }
    } else if(Check.isArray(value)) {
      return new ArrayEvaluator(value.map(item => this.parsePropertyValue(item)));
    } else {
      return new SimpleValueEvaluator(value);
    }
  }

  private _createStatementsEvaluators(statementsDefinitions): IStatementEvaluator[] {
    return (statementsDefinitions || []).map(statementDefinition => this.createStatementEvaluator(statementDefinition))
  }

  createStatementEvaluator(statementDefinition: any): IStatementEvaluator {
    return new StatementEvaluator(statementDefinition);
  }

  createLogicalOperator(logicalOperatorName: string, rules: ISegmentationRule[]): ILogicalOperator {
    switch (logicalOperatorName.toUpperCase()) {
      case 'OR':
        return new OrLogicalOperator(rules);
      case 'AND':
        return new AndLogicalOperator(rules);
      default: {
        throw new Error(`Unrecognized logical operator ${logicalOperatorName} for statement`);
      }
    }
  }

  createSegmentationRule(ruleDefinition: any): ISegmentationRule {
    return new SegmentationRule(ruleDefinition);
  }

  createComparisonOperator(operatorName: string): IComparisonOperator {
    switch (operatorName.toLowerCase()) {
      case 'equal':
      case '=':
        return new EqualOperator();
      case 'not_equal':
      case 'not equal':
      case '!=':
        return new NotEqualOperator();
      case 'is_falsy':
      case 'is falsy':
        return new IsFalsyOperator();
      case 'greater_than':
        return new GreaterThanOperator(this.dataProvider.logger);
      case 'less_than':
        return new LessThanOperator(this.dataProvider.logger);
      case "any_of":
        return new AnyOfOperator(this.dataProvider.logger);
      default:
        throw new Error(`Can't handle comparison operator ${operatorName}`);
    }
  }

  private _hasStatementsOrDefaultValue(obj: unknown): obj is {statements?: unknown, defaultValue?: unknown} {
    if(!Check.isNullOrUndefined(obj['statements'])
      || !Check.isNullOrUndefined(obj['defaultValue'])) {
      return true;
    } else {
      return false;
    }
  }

  getCurrentPropertyPath(): string {
    return '';
  }

  createPropertyContext(propertyName: string): IConfigurationEvaluationContext {
    return new PropertyLevelContext(this, propertyName);
  }

  applyValueTransformation(propertyPath: string, value: any): any {
    if(!Check.isString(value)) {
      return value;
    }
    return updateAssetPath(value, this.dataProvider.configurationPath);
  }

  createPropertyEvaluator(propertyName: string, propertyOwner: any): IPropertyEvaluator {
    switch (propertyName) {
      case 'gamesId':
        return new GamesIdPropertyEvaluator(propertyOwner, this.dataProvider.admin);
      case 'gamesSettings':
        let propertyValue = propertyOwner.gamesSettings;
        if (propertyOwner.gamesSettings && propertyOwner.gamesSettings.gamesTags) {
          propertyValue = {...propertyValue, gamesTags: this._mergeGamesFromLauncher(propertyOwner.gamesSettings.gamesTags)}
        } else {
          propertyValue = {...propertyValue, gamesTags: this._mergeGamesFromLauncher(this.dataProvider.admin && this.dataProvider.admin.gamesTags)}
        }
        return new PropertyEvaluator(propertyName, this.parsePropertyValue(propertyValue));
      default:
        return new PropertyEvaluator(propertyName, this.parsePropertyValue(propertyOwner[propertyName]));
    }
  }


  private _mergeGamesFromLauncher(existingGamesTags: any): any {

    const demoSettings = {
      demoCTAAnonymous: {pc: true, mobile: true},
      demoCTAAuthorized: {pc: true, mobile: true},
      intermediatePageAnonymous: {pc: true, mobile: true},
      intermediatePageAuthorized: {pc: true, mobile: true}
    };

    existingGamesTags = existingGamesTags || {};

    if(isInsideAutomation()) {
      Object.keys(existingGamesTags).forEach(gameType => {
        existingGamesTags[gameType]['demoSettings'] = demoSettings;
      });
    }

    let launcherGameTypes = this.getLauncherGames();

    launcherGameTypes.forEach(gameType => {
      if(existingGamesTags[gameType]) {
        existingGamesTags[gameType]['demoSettings'] = demoSettings;
      } else {
        existingGamesTags[gameType] = {};
        existingGamesTags[gameType]['demoSettings'] = demoSettings;
      }
    });

    return existingGamesTags;
  }

  getLauncherGames(): number[] {
    return this.dataProvider.getLauncherGames() || [];
  }
}
